/**
 * The people-data vendor, as the UI sees it — plan §17.5; plans/contactout-implementation-plan.md §5.6.
 *
 * Vendor truth lives on the API (`PeopleDataProvider.describe()`), never in
 * TSX: every sentence that states what a click costs is either a `pricing.*`
 * string shown verbatim or composed from `displayName`. Components that
 * already receive a descriptor inside their payload (`GET /accounts/:id`,
 * the run payload, `POST /prospect/discover`, the job-change payload) pass it
 * to `useVendor(preferred)` and no request is made; surfaces with no such
 * payload — the research-task launcher, the Accounts tabs, the Enroll
 * wizard's empty state — fall back to one cached `GET /prospect/vendor`.
 *
 * Why a module-level cache and not context: the dial changes once a quarter
 * and needs an admin; every consumer wanting the same answer for the life of
 * the tab is the common case, and `invalidateVendor()` covers the one place
 * in this SPA that can change it (Admin → Policy / Connectors).
 */

import { useEffect, useState } from 'react';
import { PEOPLE_VENDORS, type PeopleVendor } from '@actos/shared';
import { api } from './api';

export type { PeopleVendor };

/** The vendor keys, in the order the admin select lists them — one tuple, shared with the API. */
export const PEOPLE_VENDOR_KEYS = PEOPLE_VENDORS;

/**
 * Provenance labels for stored data. A contact revealed by Amplemarket last
 * year is still "from Amplemarket" after the dial moves to ContactOut, so the
 * Contacts page needs BOTH names whatever the current vendor is. The current
 * vendor's `displayName` is authoritative everywhere else.
 */
export const SOURCE_LABELS: Record<PeopleVendor, string> = {
  amplemarket: 'Amplemarket',
  contactout: 'ContactOut',
};

export interface VendorCapabilities {
  maxFilterOptions: number;
  maxPageSize: number;
  /** false ⇒ the Search buttons estimate and confirm before spending. */
  searchIsFree: boolean;
  countPeople: boolean;
  companySearch: boolean;
  batchEnrich: boolean;
}

/** Complete sentences. Shown verbatim — never split, trimmed or re-punctuated here. */
export interface VendorPricing {
  search: string;
  previewNote: string;
  reveal: string;
  revealMiss: string;
  enrich: string;
  jobCheck: string;
  discover: string;
}

export interface VendorDescriptor {
  key: PeopleVendor;
  displayName: string;
  pricing: VendorPricing;
  capabilities: VendorCapabilities;
}

/** `GET /prospect/vendor`. */
export interface VendorStatus {
  vendor: VendorDescriptor;
  connected: boolean;
  /** The resolver's sentence naming Admin → Connectors when `connected` is false. */
  reason: string | null;
}

/** `GET /prospect/accounts/:id/search-estimate`. */
export interface SearchEstimate {
  vendor: PeopleVendor;
  searchIsFree: boolean;
  /** The MAXIMUM search credits the next run can spend (Σ min(page size, matches)). */
  estimatedCredits: number;
  pageSize?: number;
  perPersona: { persona: string; matches: number | null }[];
}

let cached: VendorStatus | null = null;
let inflight: Promise<VendorStatus> | null = null;
/** Bumped by `invalidateVendor()` so mounted hooks re-fetch. */
let generation = 0;
const listeners = new Set<() => void>();

export function loadVendor(): Promise<VendorStatus> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = api<VendorStatus>('/prospect/vendor')
      .then((status) => {
        cached = status;
        return status;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Called after Admin saves `prospecting.vendor` or a vendor credential. */
export function invalidateVendor(): void {
  cached = null;
  inflight = null;
  generation += 1;
  for (const notify of listeners) notify();
}

/** Test seam — vitest has no DOM, so the cache is reset between cases. */
export function resetVendorCacheForTests(): void {
  cached = null;
  inflight = null;
  generation = 0;
  listeners.clear();
}

export interface VendorHook {
  vendor: VendorDescriptor | null;
  /** null while unknown (loading, or a payload-supplied vendor that carries no status). */
  connected: boolean | null;
  reason: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * @param preferred A descriptor already in hand from a payload. When given, no
 *   request is made and `connected` is null — the payload's own
 *   `available`/`reason` fields are the authority on that surface.
 */
export function useVendor(preferred?: VendorDescriptor | null): VendorHook {
  const [status, setStatus] = useState<VendorStatus | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(generation);

  useEffect(() => {
    const notify = () => setTick(generation);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);

  useEffect(() => {
    if (preferred) return;
    let live = true;
    setError(null);
    void loadVendor()
      .then((s) => {
        if (live) setStatus(s);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [preferred, tick]);

  if (preferred) {
    return { vendor: preferred, connected: null, reason: null, loading: false, error: null };
  }
  return {
    vendor: status?.vendor ?? null,
    connected: status ? status.connected : null,
    reason: status?.reason ?? null,
    loading: status === null && error === null,
    error,
  };
}

// ---- Pure copy helpers (tested in useVendor.test.ts) ----------------------

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The confirm shown before a BILLED search (`capabilities.searchIsFree === false`). */
export function searchConfirmText(vendor: VendorDescriptor, estimate: SearchEstimate): string {
  const lines = estimate.perPersona
    .map((p) =>
      p.matches === null
        ? `• ${p.persona}: match count unavailable`
        : `• ${p.persona}: ${p.matches} ${p.matches === 1 ? 'match' : 'matches'}`,
    )
    .join('\n');
  return (
    `Search ${vendor.displayName}? Up to ${plural(estimate.estimatedCredits, 'search credit')} will be spent.\n` +
    `${vendor.pricing.search}\n\n${lines}\n\n` +
    'No email address is revealed by searching, and your CRM mirror is checked for free.'
  );
}

/** The confirm shown before Add reveals addresses. Names the RUN's vendor. */
export function revealConfirmText(vendor: VendorDescriptor, billable: number, free: number): string {
  return (
    `Reveal ${billable} email address${billable === 1 ? '' : 'es'}? ` +
    `${vendor.pricing.reveal} This bills up to ${plural(billable, `${vendor.displayName} credit`)} and cannot be undone.` +
    (free > 0 ? ` (${plural(free, 'other')} import free from your CRM.)` : '')
  );
}

/** The sentence under an empty run. */
export function emptyRunText(vendor: VendorDescriptor): string {
  return (
    `That run returned nobody at this domain matching your approved personas — on ${vendor.displayName} or in your CRM mirror. ` +
    (vendor.capabilities.searchIsFree
      ? 'Nothing was billed. '
      : 'A search that returns nobody spends no search credits. ') +
    'A persona with different titles, or a research task, is the way forward.'
  );
}

/** `{ search: 1993, email: 2000 }` → "1,993 search · 2,000 email". */
export function formatCredits(credits: Record<string, number>): string {
  return Object.entries(credits)
    .map(([pool, n]) => `${n.toLocaleString('en-US')} ${pool.replace(/_/g, ' ')}`)
    .join(' · ');
}
